import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Loader2, QrCode, Shield, Copy, Rocket, ArrowRight } from 'lucide-react';

function AuthForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTOTP, setShowTOTP] = useState(false);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setTotpCode('');

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Authentication failed');

      const { data: factors } = await supabase.auth.mfa.listFactors();

      if (!factors.totp.length) {
        const { data: { id, secret, uri }, error: setupError } =
          await supabase.auth.mfa.enroll({
            factorType: 'totp',
            issuer: 'SpaceQuote',
            friendlyName: email
          });
        if (setupError) throw setupError;
        setFactorId(id);
        setTotpSecret(secret);
        setQrCode(uri);
        setShowTOTP(true);
        setLoading(false);
        setError('Two-factor authentication setup is required to continue.');
      } else {
        const factor = factors.totp[0];
        const { data: { id }, error: challengeError } =
          await supabase.auth.mfa.challenge({ factorId: factor.id });
        if (challengeError) throw challengeError;
        setChallengeId(id);
        setVerifying2FA(true);
        setLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  const handleTOTPSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId || !totpCode) return;
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.mfa.verify({ factorId, code: totpCode });
      if (error) throw error;
      setSetupComplete(true);
    } catch (err) {
      if (err instanceof Error && err.message.includes('name_conflict')) {
        setError('This email is already associated with a 2FA device.');
      } else {
        setError(err instanceof Error ? err.message : 'Invalid code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId || !totpCode) return;
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.mfa.verify({ challengeId, code: totpCode });
      if (error) throw error;
      setTotpCode('');
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (totpSecret) navigator.clipboard.writeText(totpSecret);
  };

  const cardClasses = "w-full max-w-[420px] bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200/60 p-8 sm:p-10";
  const inputClasses = "mt-1.5 block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all duration-150";
  const btnClasses = "w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-[15px] font-semibold text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 transition-colors duration-150";
  const errorClasses = "text-[13px] text-red-600 bg-red-50 rounded-lg px-3 py-2";

  const logoHeader = (
    <div className="flex flex-col items-center mb-8">
      <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-violet-500/25">
        <Rocket className="w-6 h-6 text-white" />
      </div>
    </div>
  );

  if (verifying2FA) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
        <div className={cardClasses}>
          {logoHeader}
          <div className="text-center mb-6">
            <div className="w-10 h-10 mx-auto mb-3 bg-violet-100 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Two-factor verification</h2>
            <p className="text-[14px] text-slate-500 mt-1.5">Enter the code from your authenticator app</p>
          </div>
          <form onSubmit={handleTOTPVerify} className="space-y-5">
            <input
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className={`${inputClasses} text-center text-2xl tracking-[0.3em] font-mono`}
              required
              autoFocus
            />
            {error && <div className={errorClasses}>{error}</div>}
            <button type="submit" disabled={loading || totpCode.length !== 6} className={btnClasses}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showTOTP) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
        <div className={cardClasses}>
          {logoHeader}
          <div className="text-center mb-6">
            <div className="w-10 h-10 mx-auto mb-3 bg-violet-100 rounded-xl flex items-center justify-center">
              <QrCode className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Set up 2FA</h2>
            <p className="text-[14px] text-slate-500 mt-1.5">Scan this QR code with your authenticator app</p>
          </div>

          {setupComplete ? (
            <div className="space-y-4 text-center">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-[14px] text-emerald-800 font-medium">2FA has been enabled for your account.</p>
              </div>
              <p className="text-[13px] text-slate-500">You'll enter a code from your authenticator app next time you sign in.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {qrCode && (
                <div className="flex justify-center p-5 bg-white rounded-xl border border-slate-200">
                  <img src={qrCode} alt="QR Code" className="w-44 h-44" />
                </div>
              )}

              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <code className="flex-1 text-[12px] font-mono text-slate-600 break-all">{totpSecret}</code>
                <button onClick={copySecret} className="p-2 text-slate-400 hover:text-violet-600 rounded-lg hover:bg-white transition-colors flex-shrink-0">
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleTOTPSetup} className="space-y-4">
                <div>
                  <label className="block text-[13px] font-medium text-slate-700 mb-1">Verification code</label>
                  <input
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className={`${inputClasses} text-center text-xl tracking-[0.3em] font-mono`}
                    required
                  />
                </div>
                {error && <div className={errorClasses}>{error}</div>}
                <button type="submit" disabled={loading || totpCode.length !== 6} className={btnClasses}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Enable 2FA'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
      <div className={cardClasses}>
        {logoHeader}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back</h2>
          <p className="text-[14px] text-slate-500 mt-1.5">Sign in to your SpaceQuote account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-[13px] font-medium text-slate-700 mb-1">Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className={inputClasses}
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-[13px] font-medium text-slate-700 mb-1">Password</label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputClasses}
            />
          </div>

          {error && <div className={errorClasses}>{error}</div>}

          <button type="submit" disabled={loading} className={btnClasses}>
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

export { AuthForm };
